﻿import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  alpha,
  Badge,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PeopleRoundedIcon from "@mui/icons-material/PeopleRounded";
import MergeRoundedIcon from "@mui/icons-material/MergeRounded";
import BuildRoundedIcon from "@mui/icons-material/BuildRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import HealthAndSafetyRoundedIcon from "@mui/icons-material/HealthAndSafetyRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import EngineeringRoundedIcon from "@mui/icons-material/EngineeringRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import KaptioLogo from "../components/KaptioLogo";
import { api } from "../api/client";
import { FLUX_HEX } from "../theme/fluxTokens";
import { SIDEBAR_WIDTH } from "./sidebarConfig";

const STORAGE_KEY = "kira-sidebar-groups";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  badgeKey?: string;
}

interface NavGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  children: NavItem[];
}

type SidebarEntry = NavItem | NavGroup;

function isNavGroup(entry: SidebarEntry): entry is NavGroup {
  return "children" in entry;
}

const SIDEBAR_ITEMS: SidebarEntry[] = [
  { label: "Dashboard", path: "/", icon: <DashboardRoundedIcon /> },
  {
    id: "engineering",
    label: "Engineering",
    icon: <EngineeringRoundedIcon />,
    children: [
      { label: "Merge Requests", path: "/merge-requests", icon: <MergeRoundedIcon /> },
      { label: "Development", path: "/development", icon: <BuildRoundedIcon /> },
      { label: "Projects", path: "/projects", icon: <FolderRoundedIcon />, badgeKey: "pendingContextUpdates" },
    ],
  },
  { label: "Developers", path: "/developers", icon: <PeopleRoundedIcon /> },
  { label: "Quality", path: "/quality", icon: <VerifiedRoundedIcon /> },
  {
    id: "operations",
    label: "Operations",
    icon: <RocketLaunchRoundedIcon />,
    children: [
      { label: "Operations", path: "/operations", icon: <RocketLaunchRoundedIcon /> },
      { label: "System Health", path: "/system-health", icon: <HealthAndSafetyRoundedIcon /> },
    ],
  },
  { label: "Settings", path: "/settings", icon: <SettingsRoundedIcon /> },
];

function loadExpandedGroups(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Record<string, boolean>;
  } catch {
    // ignore corrupt localStorage data
  }
  return {};
}

function saveExpandedGroups(state: Record<string, boolean>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
}

const BADGE_POLL_INTERVAL_MS = 60_000;

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingContextUpdates, setPendingContextUpdates] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(loadExpandedGroups);

  useEffect(() => {
    let active = true;
    const fetchCount = () => {
      api
        .getContextUpdates(1, 1, "pending")
        .then((res) => {
          if (active) setPendingContextUpdates(res.total);
        })
        .catch((err: unknown) => {
          console.error("Failed to fetch pending context updates", err);
        });
    };
    fetchCount();
    const interval = setInterval(fetchCount, BADGE_POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, []);

  const badgeCounts: Record<string, number> = {
    pendingContextUpdates,
  };

  const isActive = useCallback((path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const activeGroupIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of SIDEBAR_ITEMS) {
      if (isNavGroup(entry) && entry.children.some((child) => isActive(child.path))) {
        ids.add(entry.id);
      }
    }
    return ids;
  }, [isActive]);

  const isGroupOpen = useCallback((groupId: string) => {
    if (activeGroupIds.has(groupId)) return true;
    return expandedGroups[groupId] ?? false;
  }, [activeGroupIds, expandedGroups]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = { ...prev, [groupId]: !isGroupOpen(groupId) };
      saveExpandedGroups(next);
      return next;
    });
  }, [isGroupOpen]);

  const handleNav = (path: string) => {
    navigate(path);
    if (isMobile) onMobileClose();
  };

  const renderNavItem = (item: NavItem, options?: { nested?: boolean }) => {
    const nested = options?.nested ?? false;
    const count = item.badgeKey ? badgeCounts[item.badgeKey] ?? 0 : 0;
    const active = isActive(item.path);
    return (
      <ListItemButton
        key={item.path}
        className={nested ? "kira-nav-child" : undefined}
        selected={active}
        onClick={() => handleNav(item.path)}
        aria-current={active ? "page" : undefined}
      >
        <ListItemIcon
          sx={{
            minWidth: nested ? 32 : 36,
            color: active ? "primary.main" : "text.secondary",
            "& .MuiSvgIcon-root": nested ? { fontSize: "1.125rem" } : undefined,
          }}
        >
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: nested ? "0.8125rem" : "0.875rem",
            fontWeight: active ? 600 : 400,
          }}
        />
        {count > 0 && (
          <Badge badgeContent={count} color="warning" max={99} sx={{ mr: 0.5 }} />
        )}
      </ListItemButton>
    );
  };

  const renderNavGroup = (group: NavGroup) => {
    const open = isGroupOpen(group.id);
    const hasActiveChild = activeGroupIds.has(group.id);
    const childCount = group.children.length;
    const panelId = `nav-group-panel-${group.id}`;

    return (
      <Box key={group.id}>
        <ListItemButton
          className="kira-nav-group"
          onClick={() => toggleGroup(group.id)}
          aria-expanded={open}
          aria-controls={panelId}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
              color: hasActiveChild ? "primary.main" : "text.secondary",
            }}
          >
            {group.icon}
          </ListItemIcon>
          <ListItemText
            primary={group.label}
            secondary={open ? undefined : `${childCount} pages`}
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: hasActiveChild ? 600 : 500,
            }}
            secondaryTypographyProps={{
              fontSize: "0.6875rem",
              lineHeight: 1.2,
              sx: { mt: 0.25 },
            }}
          />
          <ChevronRightRoundedIcon
            fontSize="small"
            aria-hidden
            sx={{
              color: "text.secondary",
              flexShrink: 0,
              transition: "transform 200ms ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </ListItemButton>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            id={panelId}
            role="group"
            aria-label={`${group.label} pages`}
            sx={{
              ml: 2.5,
              mr: 1,
              mb: 0.5,
              pl: 1.5,
              borderLeft: "2px solid",
              borderColor: hasActiveChild
                ? alpha(FLUX_HEX.agentsAccent, 0.55)
                : "divider",
              borderRadius: 0,
            }}
          >
            <List disablePadding dense component="div">
              {group.children.map((child) => renderNavItem(child, { nested: true }))}
            </List>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const drawerContent = (
    <>
      <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <KaptioLogo height={22} variant="light" />
        <Typography variant="caption" color="text.secondary" sx={{ pl: 0.25 }}>
          Kira Admin
        </Typography>
      </Box>
      <Box sx={{ px: 1, pb: 2 }}>
        <Typography
          component="p"
          variant="caption"
          sx={{
            px: 2,
            pb: 1,
            display: "block",
            color: "text.secondary",
            fontWeight: 600,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontSize: "0.6875rem",
          }}
        >
          Menu
        </Typography>
        <List
          disablePadding
          component="nav"
          aria-label="Main navigation"
          sx={{ "& > li + li": { mt: 0.25 } }}
        >
          {SIDEBAR_ITEMS.map((entry, index) => {
            const showDivider =
              index > 0
              && !isNavGroup(entry)
              && entry.path === "/developers";
            return (
              <Box component="li" key={isNavGroup(entry) ? entry.id : entry.path} sx={{ listStyle: "none" }}>
                {showDivider && (
                  <Divider sx={{ my: 1, mx: 1.5, borderColor: "divider" }} />
                )}
                {isNavGroup(entry) ? renderNavGroup(entry) : renderNavItem(entry)}
              </Box>
            );
          })}
        </List>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH, boxSizing: "border-box" },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: SIDEBAR_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": { width: SIDEBAR_WIDTH, boxSizing: "border-box" },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export function SidebarMenuButton({ onClick }: { onClick: () => void }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  if (!isMobile) return null;
  return (
    <IconButton
      color="inherit"
      aria-label="open navigation"
      edge="start"
      onClick={onClick}
      sx={{ mr: 1 }}
    >
      <MenuIcon />
    </IconButton>
  );
}
